import { lazy } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { IconaTutorial } from '@/components/tutorial/IconaTutorial';
import { StorageKey } from '@/lib/storage';

import { GiocaSettemezzo } from './GiocaSettemezzo';
import { TavoliMieiSettemezzo } from './TavoliMieiSettemezzo';

/**
 * Il livello del tutorial, PIGRO e creato qui, a livello di modulo (I-1218): questo file esce dalla produzione col flag (v. sotto), quindi il chunk del tutorial con lui. L'annotazione PURE e` NECESSARIA
 * (`components/tutorial/IconaTutorial.tsx`): senza, `lazy(...)` resta nell'entry anche quando il modulo non serve.
 */
const LivelloLazy = /*#__PURE__*/ lazy(() => import('./TutorialSettemezzo').then((m) => ({ default: m.TutorialSettemezzo })));

/**
 * Il livello dentro un `ErrorBoundary` LOCALE, come il guscio dei minigiochi (`components/minigiochi/GuscioProtetto.tsx`): un chunk che non si carica o un render che lancia non butta giu la Hub (ne la rotta,
 * ne il `errorElement` della radice). ⚠️ Il ripiego e` un frammento VUOTO e non `null`: `ErrorBoundary` fa `fallback ?? <schermata d'errore a tutto schermo>`, e con `null` la Hub sparirebbe sotto «Something went wrong».
 * Col ripiego vuoto il tutorial non si apre e la Hub resta com'e`; per riprovare si esce e si rientra nella Hub (il livello resta «aperto» per l'icona finche` non si rimonta).
 */
function LivelloTutorial({ onChiudi }: { onChiudi: () => void }) {
  return (
    <ErrorBoundary fallback={<></>}>
      <LivelloLazy onChiudi={onChiudi} />
    </ErrorBoundary>
  );
}

/**
 * La Hub del sette e mezzo (sul modello di `HubScopa`): col gioco attivo = Sette e mezzo la Hub non ha la riga delle sezioni, ha DUE blocchi — «Gioca» (Gioca, Invita un amico, Entra con un
 * codice) e «I miei tavoli / Riprendi» — dentro UN solo scorrevole. Ranked e tornei vengono dopo. La stessa Hub e la pagina `/settemezzo` (`routes/settemezzo/SettemezzoHome.tsx`). In testa
 * l'icona «Come si gioca» del tutorial (col pallino finche non e` stato aperto una volta): l'unico punto da cui si monta, mai una partita viva.
 *
 * ⚠️ Lo montano `HubSections` e la rotta SOLO dentro il confronto LETTERALE del flag `VITE_FEATURE_SETTEMEZZO_ENABLED` (che il bundler piega): questo file e il marcatore
 * `data-hub-settemezzo` non sono in produzione. La serratura (`beta`, Limulo acceso) sta in `HubSections`, nel selettore e nel `RoleGate` della rotta.
 */
export function HubSettemezzo() {
  const { t } = useTranslation('settemezzo');
  return (
    <div data-hub-settemezzo className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain py-2 [touch-action:pan-y]">
      {/* px-2 e non px-1: l'area di tocco di 44 dell'icona esce di 6 px dal disegno da 32, e il contenitore scorrevole (overflow-y-auto taglia anche in x) la taglierebbe a destra */}
      <div data-riga-tutorial className="flex justify-end px-2">
        <IconaTutorial Livello={LivelloTutorial} chiave={StorageKey.SettemezzoTutorialVisto} nome={t('tutorial.icona.nome')} nuovo={t('tutorial.icona.nuovo')} />
      </div>
      <GiocaSettemezzo />
      <TavoliMieiSettemezzo />
    </div>
  );
}
